/**
 * CTR Buy Box Section JavaScript
 * Handles image gallery sliders with Swiper, plus an optional fullscreen viewer.
 */

// Portrait slides would stretch the autoHeight slider, so below this ratio they crop.
const CTR_TALL_RATIO = 0.9;

// Shopify CDN honours ?width=, so the viewer never pulls the full-size original.
function ctrResizedSrc(src, width) {
  if (!src) return src;
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (_) {
    return src;
  }
}

// Liquid's image_tag emits a srcset; slides built here need their own or they
// render soft on high-DPI screens.
function ctrSrcset(src, widths) {
  return widths.map((w) => ctrResizedSrc(src, w) + ' ' + w + 'w').join(', ');
}

const CTR_MAIN_WIDTHS = [400, 600, 800, 1000, 1200, 1600, 2000];
const CTR_THUMB_WIDTHS = [100, 150, 200, 300, 400];
const CTR_VIEWER_WIDTHS = [800, 1200, 1600, 2000, 2400];
const CTR_MAIN_SIZES = '(min-width: 750px) 50vw, 100vw';
const CTR_THUMB_SIZES = '(min-width: 750px) 12vw, 25vw';
const CTR_VIEWER_SIZES = '100vw';

function ctrEscapeAttr(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const CTR_ICON_CLOSE =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>';
const CTR_ICON_PREV =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"></path></svg>';
const CTR_ICON_NEXT =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"></path></svg>';

class CtrBuyBoxLightbox {
  static get() {
    if (!CtrBuyBoxLightbox._instance) {
      CtrBuyBoxLightbox._instance = new CtrBuyBoxLightbox();
    }
    return CtrBuyBoxLightbox._instance;
  }

  constructor() {
    this.slider = null;
    this.isOpen = false;
    this.lastFocus = null;
    this.scrollY = 0;

    this.root = document.createElement('div');
    this.root.className = 'ctr-lightbox';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Product image viewer');
    this.root.hidden = true;
    this.root.innerHTML = [
      '<div class="ctr-lightbox__backdrop" data-ctr-lightbox-close></div>',
      '<button class="ctr-lightbox__close" type="button" aria-label="Close image viewer" data-ctr-lightbox-close>',
      CTR_ICON_CLOSE,
      '</button>',
      '<p class="ctr-lightbox__counter" aria-live="polite"></p>',
      '<div class="swiper ctr-lightbox__slider"><div class="swiper-wrapper"></div></div>',
      '<button class="ctr-lightbox__nav ctr-lightbox__prev" type="button" aria-label="Previous image">' + CTR_ICON_PREV + '</button>',
      '<button class="ctr-lightbox__nav ctr-lightbox__next" type="button" aria-label="Next image">' + CTR_ICON_NEXT + '</button>',
    ].join('');

    document.body.appendChild(this.root);

    this.wrapper = this.root.querySelector('.swiper-wrapper');
    this.counter = this.root.querySelector('.ctr-lightbox__counter');

    this.root.addEventListener('click', (event) => {
      if (event.target.closest('[data-ctr-lightbox-close]')) this.close();
    });

    this.onKeydown = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowLeft' && this.slider) {
        this.slider.slidePrev();
      } else if (event.key === 'ArrowRight' && this.slider) {
        this.slider.slideNext();
      }
    };
    document.addEventListener('keydown', this.onKeydown);
  }

  renderSlides(images) {
    this.wrapper.innerHTML = images
      .map((image) => {
        const src = ctrEscapeAttr(ctrResizedSrc(image.src, 1600));
        const alt = ctrEscapeAttr(image.alt || '');
        const res =
          ' srcset="' + ctrEscapeAttr(ctrSrcset(image.src, CTR_VIEWER_WIDTHS)) +
          '" sizes="' + CTR_VIEWER_SIZES + '"';
        // Swiper's zoom binds per-slide, so tall slides scroll natively instead.
        const inner = image.tall
          ? '<div class="ctr-lightbox__scroll"><img src="' + src + '" alt="' + alt + '"' + res + '></div>'
          : '<div class="swiper-zoom-container"><img src="' + src + '" alt="' + alt + '"' + res + '></div>';
        return '<div class="swiper-slide' + (image.tall ? ' ctr-lightbox__slide--tall' : '') + '">' + inner + '</div>';
      })
      .join('');
  }

  updateCounter() {
    if (!this.slider || !this.counter) return;
    const total = this.slider.slides.length;
    this.counter.textContent = total > 1 ? this.slider.activeIndex + 1 + ' / ' + total : '';
  }

  open(images, index) {
    if (!Array.isArray(images) || !images.length) return;
    if (typeof Swiper === 'undefined') return;

    this.lastFocus = document.activeElement;
    this.renderSlides(images);
    this.root.hidden = false;

    this.scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '-' + this.scrollY + 'px';
    document.body.classList.add('ctr-lightbox-open');

    if (this.slider) this.slider.destroy(true, true);
    this.slider = new Swiper(this.root.querySelector('.ctr-lightbox__slider'), {
      initialSlide: Math.max(0, Math.min(index || 0, images.length - 1)),
      spaceBetween: 24,
      slidesPerView: 1,
      zoom: { maxRatio: 3, toggle: true },
      navigation: {
        nextEl: this.root.querySelector('.ctr-lightbox__next'),
        prevEl: this.root.querySelector('.ctr-lightbox__prev'),
      },
      on: {
        slideChange: () => this.updateCounter(),
      },
    });

    this.isOpen = true;
    this.updateCounter();
    requestAnimationFrame(() => this.root.classList.add('is-visible'));

    const closeBtn = this.root.querySelector('.ctr-lightbox__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.classList.remove('is-visible');
    document.body.classList.remove('ctr-lightbox-open');
    document.body.style.top = '';

    // base.css sets html{scroll-behavior:smooth}, which would animate this restore
    // as a visible scroll back down the page.
    const html = document.documentElement;
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, this.scrollY);
    html.style.scrollBehavior = prevBehavior;

    const finish = () => {
      this.root.hidden = true;
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
      this.wrapper.innerHTML = '';
    };
    setTimeout(finish, 200);

    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus({ preventScroll: true });
    }
  }
}

class CtrBuyBoxGallery {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.mainSlider = null;
    this.thumbSlider = null;
    this.zoomBound = false;

    this.init();
    this.markTallSlides();
    this.bindZoom();

    section.dispatchEvent(
      new CustomEvent('ctr-buy-box:ready', { bubbles: true, detail: { gallery: this } })
    );
  }

  init() {
    this.initThumbnailSlider();
    this.initMainSlider();
  }

  initThumbnailSlider() {
    const thumbEl = this.section.querySelector('.ctr-buy-box__thumbnails');
    if (!thumbEl) return;

    const slidesPerViewDesktop = 4;
    const slidesPerViewMobile = 3;

    this.thumbSlider = new Swiper(thumbEl, {
      spaceBetween: 16,
      slidesPerView: slidesPerViewMobile,
      watchSlidesProgress: true,
      navigation: {
        nextEl: this.section.querySelector('.ctr-buy-box__thumb-next'),
        prevEl: this.section.querySelector('.ctr-buy-box__thumb-prev'),
      },
      breakpoints: {
        750: {
          slidesPerView: slidesPerViewDesktop,
        },
      },
    });
  }

  initMainSlider() {
    const mainEl = this.section.querySelector('.ctr-buy-box__main-slider');
    if (!mainEl) return;

    this.mainSlider = new Swiper(mainEl, {
      spaceBetween: 0,
      slidesPerView: 1,
      autoHeight: true,
      navigation: {
        nextEl: this.section.querySelector('.ctr-buy-box__main-next'),
        prevEl: this.section.querySelector('.ctr-buy-box__main-prev'),
      },
      thumbs: {
        swiper: this.thumbSlider,
      },
    });
  }

  zoomEnabled() {
    // Read live: the A/B engine toggles this attribute at runtime.
    return this.section.hasAttribute('data-ctr-zoom-enabled');
  }

  markTallSlides() {
    const slides = this.section.querySelectorAll('.ctr-buy-box__main-slider .swiper-slide');
    slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      const apply = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;
        img.style.removeProperty('aspect-ratio');
        const tall = img.naturalWidth / img.naturalHeight < CTR_TALL_RATIO;
        slide.toggleAttribute('data-ctr-tall', tall);
        if (this.mainSlider) this.mainSlider.update();
      };
      if (img.complete) apply();
      else img.addEventListener('load', apply, { once: true });
    });
  }

  currentIndex() {
    return this.mainSlider ? this.mainSlider.activeIndex : 0;
  }

  getImages() {
    return Array.from(this.section.querySelectorAll('.ctr-buy-box__main-slider .swiper-slide'))
      .map((slide) => {
        const img = slide.querySelector('img');
        if (!img) return null;
        return {
          src: img.getAttribute('src') || img.currentSrc || '',
          alt: img.getAttribute('alt') || '',
          tall: slide.hasAttribute('data-ctr-tall'),
        };
      })
      .filter((image) => image && image.src);
  }

  bindZoom() {
    const mainEl = this.section.querySelector('.ctr-buy-box__main-slider');
    if (!mainEl || this.zoomBound) return;
    this.zoomBound = true;

    let startX = 0;
    let startY = 0;
    mainEl.addEventListener(
      'pointerdown',
      (event) => {
        startX = event.clientX;
        startY = event.clientY;
      },
      { passive: true }
    );

    mainEl.addEventListener('click', (event) => {
      if (!this.zoomEnabled()) return;
      if (!event.target.closest('.swiper-slide img')) return;
      // A swipe ends in a click too; only a near-stationary press should open.
      if (Math.abs(event.clientX - startX) > 8 || Math.abs(event.clientY - startY) > 8) return;
      event.preventDefault();
      CtrBuyBoxLightbox.get().open(this.getImages(), this.currentIndex());
    });
  }

  setImages(images) {
    if (!Array.isArray(images) || !images.length) return false;

    const mainWrap = this.section.querySelector('.ctr-buy-box__main-slider .swiper-wrapper');
    const thumbWrap = this.section.querySelector('.ctr-buy-box__thumbnails .swiper-wrapper');
    if (!mainWrap) return false;

    // Hold the outgoing ratio across the swap; markTallSlides drops it per image once the real one is available.
    const outgoing = this.section.querySelector('.ctr-buy-box__main-slider .swiper-slide img');
    let holdRatio = '';
    if (outgoing) {
      if (outgoing.naturalWidth && outgoing.naturalHeight) {
        holdRatio = outgoing.naturalWidth + ' / ' + outgoing.naturalHeight;
      } else {
        const box = outgoing.getBoundingClientRect();
        if (box.width && box.height) holdRatio = box.width + ' / ' + box.height;
      }
    }
    const hold = holdRatio ? ' style="aspect-ratio: ' + holdRatio + '"' : '';

    if (this.mainSlider) {
      this.mainSlider.destroy(true, true);
      this.mainSlider = null;
    }
    if (this.thumbSlider) {
      this.thumbSlider.destroy(true, true);
      this.thumbSlider = null;
    }

    mainWrap.innerHTML = images
      .map((image, i) => {
        const src = ctrEscapeAttr(ctrResizedSrc(image.src, 1200));
        const alt = ctrEscapeAttr(image.alt || '');
        const res =
          ' srcset="' + ctrEscapeAttr(ctrSrcset(image.src, CTR_MAIN_WIDTHS)) +
          '" sizes="' + CTR_MAIN_SIZES + '"';
        const loading = i === 0 ? 'loading="eager" fetchpriority="high"' : 'loading="lazy"';
        // Kept so ctr-intro-variants-dropdown.js can still find the hero image.
        const hook = i === 0 ? ' data-ctr-buy-box-gallery-main' : '';
        return '<div class="swiper-slide"><img src="' + src + '" alt="' + alt + '"' + res + hold + ' ' + loading + hook + '></div>';
      })
      .join('');

    if (thumbWrap) {
      thumbWrap.innerHTML = images
        .map((image, i) => {
          const thumbSrc = image.thumb || image.src;
          const src = ctrEscapeAttr(ctrResizedSrc(thumbSrc, 400));
          const alt = ctrEscapeAttr(image.alt || '');
          const res =
            ' srcset="' + ctrEscapeAttr(ctrSrcset(thumbSrc, CTR_THUMB_WIDTHS)) +
            '" sizes="' + CTR_THUMB_SIZES + '"';
          const hook = i === 0 ? ' data-ctr-buy-box-gallery-thumb' : '';
          return '<div class="swiper-slide"><img src="' + src + '" alt="' + alt + '"' + res + ' loading="lazy"' + hook + '></div>';
        })
        .join('');
    }

    this.init();
    this.markTallSlides();
    return true;
  }

  destroy() {
    if (this.mainSlider) {
      this.mainSlider.destroy(true, true);
    }
    if (this.thumbSlider) {
      this.thumbSlider.destroy(true, true);
    }
  }
}

function ctrInitBuyBox(section) {
  if (section.buyBoxGallery) return section.buyBoxGallery;
  section.buyBoxGallery = new CtrBuyBoxGallery(section);
  return section.buyBoxGallery;
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.ctr-buy-box');
  sections.forEach((section) => {
    ctrInitBuyBox(section);
  });
  window.ctrBuyBoxReady = true;
  document.dispatchEvent(new CustomEvent('ctr-buy-box:all-ready'));
});

// Reinitialize on Shopify section reload (Theme Editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.ctr-buy-box');
    if (section) {
      section.buyBoxGallery = null;
      ctrInitBuyBox(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.ctr-buy-box');
    if (section && section.buyBoxGallery) {
      section.buyBoxGallery.destroy();
      section.buyBoxGallery = null;
    }
  });
}

// Accordion functionality - ensure only one open at a time
document.addEventListener('DOMContentLoaded', () => {
  const accordionContainers = document.querySelectorAll('.ctr-buy-box__accordions');

  accordionContainers.forEach(container => {
    const accordions = container.querySelectorAll('.ctr-buy-box__accordion');

    accordions.forEach(accordion => {
      accordion.addEventListener('toggle', (e) => {
        if (accordion.open) {
          // Close other accordions in the same container
          accordions.forEach(other => {
            if (other !== accordion && other.open) {
              other.open = false;
            }
          });
        }
      });
    });
  });
});
